import { Button, Form, message, Select, Tag, Upload } from "antd";
import debounce from "lodash.debounce";
import { useEffect, useState } from "react";

import { CheckCircleOutlined, UploadOutlined } from "@ant-design/icons";

import { getAdminsList } from "../../../../services/adminApi";
import {
  mergeLoadApi,
  multipleLoadApi,
} from "../../../../services/multipleLoadApi";
import { getCompanyUsers } from "../../../../services/userAPI";
import AdminDTO from "../../../../shared/dtos/adminDTO";
import { TripDataMultipleLoad } from "../../../../shared/dtos/multipleLoadDTO";
import ServiceTypeDTO from "../../../../shared/dtos/serviceTypeDTO";
import UserDTO from "../../../../shared/dtos/userDTO";
import { disabledDateBeforeToday } from "../../../../utils/helpers/disabledDateBeforeToday";
import DatePicker from "../../../common/DatePicker";
import { useServicesTypes } from "../../../common/hooks/useServicesTypes";
import { readUploadXLSXFile } from "../../../groupTrip/common/utils";
import { parseDataFromXMLSMultipleLoad } from "../utils";

interface Props {
  setData: (data: any) => void;
  formattedData: TripDataMultipleLoad[];
  setFormattedData: (data: any) => void;
  setSelectedIds: (value: Array<number>) => void;
  selectedIds: Array<number>;
}

const FormMultipleLoad = ({
  setData,
  formattedData,
  setFormattedData,
  setSelectedIds,
  selectedIds,
}: Props): JSX.Element => {
  const [fileList, setFileList] = useState<Array<any>>([]);
  const [adminsList, setAdminsList] = useState<Array<AdminDTO>>([]);
  const [users, setUsers] = useState<Array<UserDTO>>([]);
  const [loading, setLoading] = useState(false);
  const [serviceTypeId, setServiceTypeId] = useState<string | undefined>();
  const [adminId, setAdminId] = useState<number | undefined>();
  const [userId, setUserId] = useState<number | undefined>();
  const [searchAdmin, setSearchAdmin] = useState("");
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [form] = Form.useForm();

  const services = useServicesTypes();

  useEffect(() => {
    const getAdmins = async () => {
      setLoadingAdmins(true);
      const admins = await getAdminsList({
        take: 100,
        filters: { name: searchAdmin },
      });
      setAdminsList(admins?.data || []);
      setLoadingAdmins(false);
    };
    getAdmins();
  }, [searchAdmin]);

  const readUploadFile = (e: any) => {
    readUploadXLSXFile(
      e,
      (data) => {
        const list = parseDataFromXMLSMultipleLoad(data);
        list.shift();
        setData(list);
      },
      false,
    );
  };

  const normFile = (e: any) => {
    if (e?.fileList && e?.fileList.length) {
      const tmp = { target: { files: [e?.file] } };
      setFileList(e?.file ? [e.file] : []);
      readUploadFile(tmp);
    } else {
      setFileList([]);
    }
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  const onMergeTrips = async () => {
    if (!serviceTypeId || !adminId) {
      message.warning(
        "No se ha seleccionado un servicio o un administrador, seleccione ambos antes de mezclar",
      );
      return;
    }

    const selectedData = formattedData.filter((data) =>
      selectedIds.includes(data.id),
    );
    const invalidData = selectedData.some(
      (i) => !i.origin?.address || !i.destiny?.address,
    );

    if (invalidData) {
      message.warning("Existen direcciones sin validar");
    } else {
      const jsonData = {
        serviceTypeId: Number(serviceTypeId),
        adminId: Number(adminId),
        data: selectedData,
        ...(userId ? { userId } : {}),
      };
      setLoading(true);
      try {
        const result = await mergeLoadApi(jsonData);
        if (result?.errorsCount === 0) {
          message.success("Los viajes han sido mezclados satisfactoriamente.");
          setFormattedData(
            formattedData.filter((x) => !selectedIds.includes(x.id)),
          );
          setSelectedIds([]);
        } else if (result?.errorsCount !== 0 && result?.successCount !== 0) {
          message.warning(
            <>
              <Tag>8 viajes se han generado satisfactoriamente.</Tag>
              <br />
              <br />
              <Tag color="red">5 no se han podido generar.</Tag>
            </>,
          );
        } else {
          message.error("Ha ocurrido un error al intentar generar los viajes.");
        }
      } catch (error) {
        message.error("Ha ocurrido un error al intentar generar los viajes.");
      } finally {
        setLoading(false);
      }
    }
  };

  const onFinish = async (values: any) => {
    const invalidData = formattedData.some(
      (i) => !i.origin?.address || !i.destiny?.address,
    );

    if (invalidData) {
      message.warning("Existen direcciones sin validar");
    } else {
      const jsonData = {
        serviceTypeId: values?.serviceTypeId,
        adminId: values?.adminId,
        data: formattedData,
        repeat: values?.weeksRepetition,
        startDate: values?.startDate?.toDate(),
        ...(userId ? { userId } : {}),
      };
      setLoading(true);
      try {
        const result = await multipleLoadApi(jsonData);
        if (result?.errorsCount === 0) {
          message.success("Los viajes han sido generados satisfactoriamente.");
          setData([]);
          setFormattedData([]);
          form.resetFields();
          setFileList([]);
        } else if (result?.errorsCount !== 0 && result?.successCount !== 0) {
          message.warning(
            <>
              <Tag>
                {result?.successCount} viajes se han generado
                satisfactoriamente.
              </Tag>
              <br />
              <br />
              <Tag color="red">
                {result.errorsCount} no se han podido generar.
              </Tag>
            </>,
          );
        } else {
          message.error("Ha ocurrido un error al intentar generar los viajes.");
        }
      } catch (error) {
        message.error("Ha ocurrido un error al intentar generar los viajes.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSearch = debounce((newSearch: string) => {
    setSearchAdmin(newSearch);
  }, 250);

  const handleAdminSelect = async (adminId: number) => {
    setAdminId(adminId);
    const users = await getCompanyUsers({ adminId });
    setUsers(users);
  };

  return (
    <Form
      form={form}
      onFinish={onFinish}
      autoComplete="off"
      layout="inline"
      disabled={loading}
    >
      <Form.Item
        name="upload"
        valuePropName="fileList"
        getValueFromEvent={normFile}
      >
        <Upload listType="picture" beforeUpload={() => false} maxCount={1}>
          {!fileList.length && (
            <Button icon={<UploadOutlined />}>Cargar archivo excel</Button>
          )}
        </Upload>
      </Form.Item>
      <Form.Item
        name="serviceTypeId"
        label="Tipo Servicio"
        tooltip="En que vehiculo desea viajar?"
        rules={[
          {
            required: true,
            message: "Por favor seleccione un servicio!",
          },
        ]}
      >
        <Select
          placeholder="Selecione un tipo"
          style={{ width: 160 }}
          options={services.map((serviceType: ServiceTypeDTO) => ({
            label: serviceType?.name,
            value: serviceType?.id,
          }))}
          onChange={(value) => setServiceTypeId(value)}
        />
      </Form.Item>
      <Form.Item
        name="adminId"
        label="Administrador"
        rules={[{ required: true, message: "Elija un administrador!" }]}
      >
        <Select
          placeholder="Selecione un administrador"
          style={{ width: 220 }}
          options={adminsList.map((admin: AdminDTO) => ({
            label: admin?.name,
            value: admin?.id,
          }))}
          onSearch={handleSearch}
          onChange={(value) => handleAdminSelect(value)}
          showSearch
          optionFilterProp="label"
          notFoundContent={null}
          filterOption={false}
          loading={loadingAdmins}
        />
      </Form.Item>
      <Form.Item name="userId" label="Usuario">
        <Select
          placeholder="Selecione un usuario"
          showSearch
          allowClear
          optionFilterProp="label"
          style={{ width: 220 }}
          options={users?.map((user: UserDTO) => ({
            label: `${user?.firstName} ${user.lastName}`,
            value: user?.id,
          }))}
          onChange={(value) => setUserId(value)}
        />
      </Form.Item>
      <Form.Item
        name="startDate"
        label="Fecha Inicio"
        rules={[{ required: true, message: "Elija fecha de inicio!" }]}
      >
        <DatePicker
          placeholder="Seleccione"
          disabledDate={disabledDateBeforeToday}
          format="DD-MM-YYYY"
          style={{ width: "100%" }}
        />
      </Form.Item>
      <Form.Item
        name="weeksRepetition"
        label="Repetir"
        rules={[{ required: true, message: "Elija cuantas semana se repite." }]}
        initialValue={1}
      >
        <Select
          placeholder="Selecione"
          style={{ width: 150 }}
          options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(
            (repeat: number) => ({
              label: `${repeat} semana${repeat !== 1 ? "s" : ""}`,
              value: repeat,
            }),
          )}
          showSearch
          notFoundContent={null}
          optionFilterProp="label"
        />
      </Form.Item>
      <Form.Item>
        {!!selectedIds.length && (
          <Button
            type="default"
            onClick={() => {
              onMergeTrips();
            }}
            disabled={!formattedData?.length}
            loading={loading}
            icon={<CheckCircleOutlined />}
          >
            Mezclar viajes
          </Button>
        )}
      </Form.Item>
      <Form.Item>
        <Button
          type="primary"
          onClick={() => {
            form.submit();
          }}
          disabled={!formattedData?.length}
          loading={loading}
          icon={<CheckCircleOutlined />}
        >
          Generar viajes
        </Button>
      </Form.Item>
    </Form>
  );
};

export default FormMultipleLoad;
